// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { InvoiceFilter } from '@/billing/api/get-invoices';
import { InvoiceAgingBucket } from '@/billing/api/types';
import { BILLING_BREADCRUMB_ROOT } from '@/billing/components/billing-nav';
import { PerCurrencySections } from '@/billing/components/currency-section-accordion';
import { useInvoiceAgingSummary } from '@/billing/hooks/use-invoice-aging-summary';
import { useInvoices } from '@/billing/hooks/use-invoices';
import {
  BUCKET_KEY_VALUES,
  type BucketKey,
  type CompanyRow,
  type CurrencyBucketCards,
  type GroupByDimension,
  type GroupedRow,
  type InvoiceRow,
  buildCompanyRows,
  buildCurrencyBucketCards,
  computeAgingFooterTotals,
  filterDataByBucket,
  filterDataBySearch,
  formatAgingAmount,
  groupDataByDimension,
  sumAgingBuckets,
} from '@/billing/utilities/aging-grouping';
import { DownloadFooter } from '@/components/download-footer';
import { EmptyState } from '@/components/empty-state';
import { FilterToggleGroup } from '@/components/filter-toggle-group';
import { PageContent } from '@/components/page-content';
import { SectionBreadcrumbs } from '@/components/section-breadcrumbs';
import { PrimaryMetric, SummaryCard } from '@/components/summary-card';
import {
  ApartmentOutlined,
  ArrowForwardRounded,
  ClearRounded,
  ExpandMoreRounded,
  SearchRounded,
  WidgetsOutlined,
} from '@/constants/hardfin-standard-icons';
import { IsoCurrencyCode } from '@/currency/constants/iso';
import { useCustomers } from '@/customer/hooks/use-customers';
import { CustomerFilter } from '@/filters/customer-filter';
import { ProjectFilter } from '@/filters/project-filter';
import { useSparseProjectList } from '@/project/hooks/use-sparse-project-list';
import { pluralize } from '@/utilities/pluralize';
import { searchTermSchema } from '@/utilities/search-term';
import {
  Box,
  Button,
  Chip,
  Collapse,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Link, createFileRoute } from '@tanstack/react-router';
import { Decimal } from 'decimal.js';
import { omit } from 'lodash-es';
import {
  type ReactNode,
  memo,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { z } from 'zod';

// ---------------------------------------------------------------------------
// Route definition
// ---------------------------------------------------------------------------

// All 5 aging buckets as the fixed scope for the invoice listing query —
// relies on listing.sql gating this filter on OPEN + PARTIALLY_PAID, so the
// UI never has to re-filter by status.
const ALL_AGING_BUCKETS: InvoiceAgingBucket[] = [
  'current',
  '1-30',
  '31-60',
  '61-90',
  '91+',
];

// -1 is the unpaginated sentinel the backend's shared listing parser
// (VisibilitySettings.UnpaginatedPageSize) opts into — the aging report
// groups client-side so it needs every aging-eligible invoice in one
// response.
const AGING_LISTING_PAGE_SIZE = -1;

type AgingFilter = BucketKey | 'all';

const AGING_FILTER_OPTIONS: { value: AgingFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'current', label: 'Current' },
  { value: '1-30', label: '1–30 days' },
  { value: '31-60', label: '31–60 days' },
  { value: '61-90', label: '61–90 days' },
  { value: '91+', label: '91+ days' },
];

const GROUP_BY_OPTIONS: {
  value: GroupByDimension;
  label: string;
  icon: ReactNode;
}[] = [
  {
    value: 'company',
    label: 'Company',
    icon: <ApartmentOutlined fontSize="small" />,
  },
  {
    value: 'project',
    label: 'Project',
    icon: <WidgetsOutlined fontSize="small" />,
  },
];

const searchSchema = z.object({
  customerIds: z.array(z.string()).optional(),
  projectIds: z.array(z.string()).optional(),
  bucket: z
    .enum([...BUCKET_KEY_VALUES, 'all'] as [string, ...string[]])
    .optional(),
  groupBy: z.enum(['company', 'project']).optional(),
  query: searchTermSchema,
});

const SEARCH_DEBOUNCE_MS = 300;

export const Route = createFileRoute('/_app/billing/aging')({
  component: ArAgingReportRoute,
  validateSearch: searchSchema,
});

// ---------------------------------------------------------------------------
// Color system — bucket-based palette tokens for dollar amounts
// ---------------------------------------------------------------------------

// Bucket colors are MUI palette tokens (not hex literals) so dark-mode /
// rebranding passes pick them up automatically. 1–30 and 31–60 both map
// to warning.dark; the visual distinction between those two buckets is
// carried by the chip variant (outlined vs filled) on the detail chip,
// not by two separately-defined ambers.
type BucketColorKey = 'current' | 'd1_30' | 'd31_60' | 'd61_90' | 'd91';

const BUCKET_COLORS: Record<BucketColorKey, string> = {
  current: 'success.dark',
  d1_30: 'warning.dark',
  d31_60: 'warning.dark',
  d61_90: 'error.dark',
  d91: 'error.main',
};

function bucketColor(bucket: BucketColorKey): string {
  return BUCKET_COLORS[bucket];
}

// Card primary metric colors (same mapping, keyed by label for card lookup)
const CARD_COLORS: Record<string, string> = {
  Current: BUCKET_COLORS.current,
  '1–30 days': BUCKET_COLORS.d1_30,
  '31–60 days': BUCKET_COLORS.d31_60,
  '61–90 days': BUCKET_COLORS.d61_90,
  '91+ days': BUCKET_COLORS.d91,
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function bucketChipProps(bucket: string): {
  color: 'success' | 'warning' | 'error' | 'default';
  variant: 'outlined' | 'filled';
} {
  switch (bucket) {
    case 'Current':
      return { color: 'success', variant: 'outlined' };
    case '1–30':
      return { color: 'warning', variant: 'outlined' };
    case '31–60':
      return { color: 'warning', variant: 'filled' };
    case '61–90':
      return { color: 'error', variant: 'outlined' };
    case '91+':
      return { color: 'error', variant: 'filled' };
    default:
      return { color: 'default', variant: 'outlined' };
  }
}

/** Format a bucket dollar value — em dash for zero, colored text otherwise */
function BucketAmount(props: {
  value: Decimal;
  currencyCode: IsoCurrencyCode;
  colorKey: 'current' | 'd1_30' | 'd31_60' | 'd61_90' | 'd91';
}) {
  if (props.value.isZero()) {
    return (
      <Typography variant="body2" color="text.disabled">
        {'\u2014'}
      </Typography>
    );
  }
  return (
    <Typography variant="body2" sx={{ color: bucketColor(props.colorKey) }}>
      {formatAgingAmount(props.value, props.currencyCode)}
    </Typography>
  );
}

/** Palette token for an invoice balance based on its bucket label. */
function invoiceBalanceColor(bucket: string): string {
  switch (bucket) {
    case 'Current':
      return BUCKET_COLORS.current;
    case '1–30':
      return BUCKET_COLORS.d1_30;
    case '31–60':
      return BUCKET_COLORS.d31_60;
    case '61–90':
      return BUCKET_COLORS.d61_90;
    case '91+':
      return BUCKET_COLORS.d91;
    default:
      return 'text.primary';
  }
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

function ArAgingReportRoute() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const activeBucket: AgingFilter = (search.bucket as AgingFilter) ?? 'all';
  const groupBy: GroupByDimension =
    (search.groupBy as GroupByDimension) ?? 'company';
  const activeQuery = search.query ?? '';
  const activeCustomerIds = useMemo(
    () => search.customerIds ?? [],
    [search.customerIds]
  );
  const activeProjectIds = useMemo(
    () => search.projectIds ?? [],
    [search.projectIds]
  );

  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  // Mirrors search.query as the controlled input value so typing stays
  // responsive while the URL update is debounced.
  const [searchInput, setSearchInput] = useState(activeQuery);
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const searchRef = useRef(search);
  searchRef.current = search;

  // Sync the controlled input when the URL query changes from an external
  // source (browser back/forward, a programmatic navigate elsewhere).
  // Skipped when the user is mid-debounce (a pending timer means the input
  // is the source of truth); otherwise the input would overwrite itself
  // on every keystroke-triggered navigate.
  useEffect(() => {
    if (searchTimeoutRef.current === null) {
      setSearchInput(activeQuery);
    }
  }, [activeQuery]);

  // Tear down any pending debounce timer on unmount so the callback does
  // not fire against a stale navigate closure after the component is gone.
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current !== null) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  // Sparse customer / project lists back the searchable dropdowns AND the
  // UUID → nano-key translation. CustomerFilter / ProjectFilter emit the
  // selected UUIDs (their internal id), but the /v1/invoice endpoint
  // filters by nano keys (`customerKey`, `projectKey`), so we map at the
  // query-building boundary using id → key lookup maps. Both hooks share
  // React Query cache with the rest of the app (5 min staleTime).
  const { customers } = useCustomers();
  const { projects } = useSparseProjectList();

  const customerKeyById = useMemo(() => {
    const map = new Map<string, string>();
    for (const c of customers) {
      map.set(c.id, c.key);
    }
    return map;
  }, [customers]);
  const projectKeyById = useMemo(() => {
    const map = new Map<string, string>();
    for (const p of projects) {
      map.set(p.id, p.key);
    }
    return map;
  }, [projects]);

  // Selected UUIDs → nano keys. Unknown ids (e.g., a URL carrying stale
  // UUIDs after a customer is deleted) are dropped so the backend doesn't
  // see phantom keys. Empty result short-circuits to undefined so the
  // filter is omitted from the query string entirely.
  const filterCustomerKeys = useMemo(() => {
    if (activeCustomerIds.length === 0) return undefined;
    const keys: string[] = [];
    for (const id of activeCustomerIds) {
      const key = customerKeyById.get(id);
      if (key !== undefined) keys.push(key);
    }
    return keys.length > 0 ? keys : undefined;
  }, [activeCustomerIds, customerKeyById]);
  const filterProjectKeys = useMemo(() => {
    if (activeProjectIds.length === 0) return undefined;
    const keys: string[] = [];
    for (const id of activeProjectIds) {
      const key = projectKeyById.get(id);
      if (key !== undefined) keys.push(key);
    }
    return keys.length > 0 ? keys : undefined;
  }, [activeProjectIds, projectKeyById]);

  // Listing — reuses the generic /v1/invoice endpoint via the shared
  // useInvoices hook, scoped to the 5 aging buckets. The SQL already gates
  // aging-bucket filtering on OPEN + PARTIALLY_PAID, so this is exactly
  // the aging-eligible universe. Customer / project keys narrow the set
  // server-side. pageSize=-1 hits the shared parser's unpaginated
  // short-circuit so the backend returns the full set with no LIMIT.
  const listingFilter: InvoiceFilter = useMemo(
    () => ({
      pageSize: AGING_LISTING_PAGE_SIZE,
      agingBuckets: ALL_AGING_BUCKETS,
      customerKeys: filterCustomerKeys,
      projectKeys: filterProjectKeys,
    }),
    [filterCustomerKeys, filterProjectKeys]
  );
  const { invoices, isLoading: listingLoading } = useInvoices({
    filter: listingFilter,
  });

  // Summary cards live on their own route so pagination / reloads on the
  // table side never affect the global totals shown at the top.
  const { summary: agingSummary } = useInvoiceAgingSummary();

  // Tier 1: company-level aggregation from the raw invoice list. Expensive
  // (group + sum + sort), only recomputes when the query result identity
  // changes. Cleanup is automatic — React releases the memoized value on
  // the next render or when the component unmounts.
  const companyData = useMemo<CompanyRow[] | null>(
    () => (invoices ? buildCompanyRows(invoices) : null),
    [invoices]
  );

  // Tier 2: pre-computed filtered views keyed by bucket (stable string
  // keys: 'all' | 'current' | '1-30' | '31-60' | '61-90' | '91+'). Built
  // once per companyData change, so flipping bucket filters is an O(1)
  // record lookup instead of re-filtering. The record is tied to the
  // memo's lifetime, so it's garbage collected when companyData changes
  // or the component unmounts — no module-level or ref-backed cache.
  const filteredByBucket = useMemo<Record<AgingFilter, CompanyRow[]>>(() => {
    const empty: Record<AgingFilter, CompanyRow[]> = {
      all: [],
      current: [],
      '1-30': [],
      '31-60': [],
      '61-90': [],
      '91+': [],
    };
    if (!companyData) {
      return empty;
    }
    return {
      all: companyData,
      current: filterDataByBucket(companyData, 'current'),
      '1-30': filterDataByBucket(companyData, '1-30'),
      '31-60': filterDataByBucket(companyData, '31-60'),
      '61-90': filterDataByBucket(companyData, '61-90'),
      '91+': filterDataByBucket(companyData, '91+'),
    };
  }, [companyData]);

  const bucketFiltered = filteredByBucket[activeBucket];

  // Tier 3: free-text narrowing over the already-bucketed rows. Memoed on
  // (bucket view, query) so toggling buckets with the same query or
  // clearing the query doesn't rebuild the whole pipeline.
  const searchFiltered = useMemo(
    () => filterDataBySearch(bucketFiltered, activeQuery),
    [bucketFiltered, activeQuery]
  );

  // Tier 4: group-by-dimension transform layered on top of the narrowed
  // rows. Cheap compared to earlier tiers, so a plain memo keyed on
  // (searchFiltered identity, groupBy) is sufficient.
  const groupedData = useMemo(
    () => groupDataByDimension(searchFiltered, groupBy),
    [searchFiltered, groupBy]
  );

  const currencyBucketCards: CurrencyBucketCards[] | null = useMemo(
    () => (agingSummary ? buildCurrencyBucketCards(agingSummary) : null),
    [agingSummary]
  );

  const toggleRow = (label: string) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(label)) {
        next.delete(label);
      } else {
        next.add(label);
      }
      return next;
    });
  };

  const handleGroupByChange = (value: GroupByDimension) => {
    setExpandedRows(new Set());
    void navigate({
      search:
        value === 'company'
          ? { ...omit(search, 'groupBy') }
          : { ...search, groupBy: value },
    });
  };

  const handleAgingFilterChange = (value: AgingFilter) => {
    void navigate({
      search:
        value === 'all'
          ? { ...omit(search, 'bucket') }
          : { ...search, bucket: value },
    });
  };

  const handleChangeCustomer = (customerIds: string[]) => {
    void navigate({
      search:
        customerIds.length === 0
          ? { ...omit(search, 'customerIds') }
          : { ...search, customerIds },
    });
  };

  const handleChangeProject = (projectIds: string[]) => {
    void navigate({
      search:
        projectIds.length === 0
          ? { ...omit(search, 'projectIds') }
          : { ...search, projectIds },
    });
  };

  const handleSearch = (query: string) => {
    setSearchInput(query);
    if (searchTimeoutRef.current !== null) {
      clearTimeout(searchTimeoutRef.current);
    }
    searchTimeoutRef.current = setTimeout(() => {
      searchTimeoutRef.current = null;
      const latestSearch = searchRef.current;
      void navigate({
        search:
          query.length === 0
            ? { ...omit(latestSearch, 'query') }
            : { ...latestSearch, query },
      });
    }, SEARCH_DEBOUNCE_MS);
  };

  const hasActiveFilters =
    activeCustomerIds.length > 0 || activeProjectIds.length > 0;

  return (
    <PageContent>
      {/* Header */}
      <Stack sx={{ mb: 2 }}>
        <SectionBreadcrumbs
          items={[BILLING_BREADCRUMB_ROOT, { label: 'A/R aging' }]}
        />
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Typography variant="h5">A/R aging</Typography>
          <Button
            component={Link}
            to="/billing/lifecycle"
            size="small"
            endIcon={<ArrowForwardRounded fontSize="small" />}
            sx={{ textTransform: 'none' }}
          >
            View billing lifecycle
          </Button>
        </Stack>
      </Stack>

      {/* Summary cards, one labeled section per currency. PrimaryMetric
          renders its own value + caption skeletons when isLoading is true,
          so the loading branch passes a null section and lets the component
          own the loading treatment. Single-currency tenants see exactly one
          section. */}
      <Box sx={{ mb: 2 }}>
        {currencyBucketCards === null ? (
          <BucketCardRow section={null} />
        ) : (
          <PerCurrencySections
            buckets={currencyBucketCards}
            renderBucket={(section) => <BucketCardRow section={section} />}
          />
        )}
      </Box>

      <Stack spacing={2}>
        {/* Toggle button groups */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <FilterToggleGroup
            value={activeBucket}
            options={AGING_FILTER_OPTIONS}
            onChange={handleAgingFilterChange}
            ariaLabel="Aging bucket filter"
          />
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Typography variant="body2" color="text.secondary">
              Group by:
            </Typography>
            <FilterToggleGroup
              value={groupBy}
              options={GROUP_BY_OPTIONS}
              onChange={handleGroupByChange}
              ariaLabel="Group by dimension"
            />
          </Stack>
        </Stack>

        {/* Filters */}
        <Stack direction="row" alignItems="center" spacing={2}>
          <CustomerFilter
            label="Company"
            value={activeCustomerIds}
            onChange={handleChangeCustomer}
          />
          <ProjectFilter
            label="Project"
            value={activeProjectIds}
            onChange={handleChangeProject}
          />
          {hasActiveFilters && (
            <Button
              size="small"
              onClick={() =>
                void navigate({
                  search: omit(search, ['customerIds', 'projectIds']),
                })
              }
            >
              Clear filters
            </Button>
          )}

          <Box flex={1} />

          <TextField
            size="small"
            placeholder="Search"
            value={searchInput}
            onChange={(e) => handleSearch(e.target.value)}
            sx={{ width: 240 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchRounded
                    fontSize="small"
                    sx={{ color: 'neutral.400' }}
                  />
                </InputAdornment>
              ),
              endAdornment: searchInput ? (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => handleSearch('')}
                    edge="end"
                    size="small"
                    aria-label="Clear search"
                  >
                    <ClearRounded fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : undefined,
            }}
          />
        </Stack>

        {/* Table — once the listing fetch has resolved, render the shared
            EmptyState pattern instead of the table (matches the invoices
            listing's empty branch so the two pages feel consistent). While
            the first fetch is still in flight, keep the skeleton table so
            we don't flash an empty state before data arrives. */}
        {companyData !== null && groupedData.length === 0 ? (
          <EmptyState
            title="No outstanding invoices"
            message="Invoices that are open or partially paid will appear here"
          />
        ) : (
          <AgingTable
            data={groupedData}
            groupBy={groupBy}
            expandedRows={expandedRows}
            onToggle={toggleRow}
            isLoading={listingLoading && !companyData}
          />
        )}

        {/* Footer: export link only. The aging report is unpaginated
            (pageSize=-1) because the client groups by company/project and
            needs every aging-eligible invoice in one response, so there's
            no pagination footer — and a trailing row count adds noise without
            telling the user anything they can't see from the table. */}
        <Stack direction="row" alignItems="center" py={2} px={1}>
          <DownloadFooter
            downloadURL={
              new URL(
                '/v1/export/ar-aging',
                import.meta.env.VITE_API_URL as string
              )
            }
            downloadLabel="Export AR aging"
            itemCount={companyData ? groupedData.length : null}
          />
        </Stack>
      </Stack>
    </PageContent>
  );
}

// Fixed card labels in display order, reused for the loading skeleton so
// the layout doesn't shift when real numbers arrive.
const SKELETON_CARD_LABELS = [
  'Current',
  '1–30 days',
  '31–60 days',
  '61–90 days',
  '91+ days',
];

// A single currency section's 5 bucket cards. A null section renders the
// loading skeleton; PrimaryMetric owns its value + caption skeletons.
function BucketCardRow(props: { section: CurrencyBucketCards | null }) {
  const { section } = props;
  if (section === null) {
    return (
      <Grid container spacing={3}>
        {SKELETON_CARD_LABELS.map((label) => (
          <Grid item xs={12} sm={6} md key={label}>
            <SummaryCard>
              <PrimaryMetric
                label={label}
                value=""
                color={CARD_COLORS[label]}
                isLoading
              />
            </SummaryCard>
          </Grid>
        ))}
      </Grid>
    );
  }
  return (
    <Grid container spacing={3}>
      {section.cards.map((card) => (
        <Grid item xs={12} sm={6} md key={card.label}>
          <SummaryCard>
            <PrimaryMetric
              label={card.label}
              value={formatAgingAmount(card.amount, section.isoCode)}
              color={CARD_COLORS[card.label]}
              isLoading={false}
              caption={pluralize(card.count, { singular: 'invoice' })}
            />
          </SummaryCard>
        </Grid>
      ))}
    </Grid>
  );
}

// ---------------------------------------------------------------------------
// Aging table with expandable rows
// ---------------------------------------------------------------------------

function AgingTable(props: {
  data: GroupedRow[];
  groupBy: GroupByDimension;
  expandedRows: Set<string>;
  onToggle: (label: string) => void;
  isLoading: boolean;
}) {
  const { data, groupBy, expandedRows, onToggle, isLoading } = props;

  const footerTotals = computeAgingFooterTotals(data);

  const firstColumnLabel = groupBy === 'company' ? 'Company' : 'Project';

  return (
    <TableContainer component={Paper} variant="outlined">
      <Table>
        <TableHead>
          <TableRow>
            <TableCell sx={{ width: 48 }} />
            <TableCell>{firstColumnLabel}</TableCell>
            <TableCell align="right">Current</TableCell>
            <TableCell align="right">1–30 days</TableCell>
            <TableCell align="right">31–60 days</TableCell>
            <TableCell align="right">61–90 days</TableCell>
            <TableCell align="right">91+ days</TableCell>
            <TableCell align="right">Total</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {isLoading && <AgingTableSkeletonRows />}
          {!isLoading &&
            data.map((row) => (
              <ExpandableGroupRow
                key={row.label}
                row={row}
                isExpanded={expandedRows.has(row.label)}
                onToggle={() => onToggle(row.label)}
              />
            ))}
        </TableBody>
        {!isLoading && data.length > 0 && (
          <TableFooter>
            {footerTotals.map((totals) => (
              <TableRow
                key={totals.isoCode}
                sx={{
                  backgroundColor: 'neutral.100',
                  '& td': {
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    borderBottom: 'none',
                  },
                }}
              >
                <TableCell />
                <TableCell>
                  {footerTotals.length > 1
                    ? `Total (${totals.isoCode})`
                    : 'Total'}
                </TableCell>
                <TableCell align="right" sx={{ color: BUCKET_COLORS.current }}>
                  {formatAgingAmount(totals.current, totals.isoCode)}
                </TableCell>
                <TableCell align="right" sx={{ color: BUCKET_COLORS.d1_30 }}>
                  {formatAgingAmount(totals.d1_30, totals.isoCode)}
                </TableCell>
                <TableCell align="right" sx={{ color: BUCKET_COLORS.d31_60 }}>
                  {formatAgingAmount(totals.d31_60, totals.isoCode)}
                </TableCell>
                <TableCell align="right" sx={{ color: BUCKET_COLORS.d61_90 }}>
                  {formatAgingAmount(totals.d61_90, totals.isoCode)}
                </TableCell>
                <TableCell align="right" sx={{ color: BUCKET_COLORS.d91 }}>
                  {formatAgingAmount(totals.d91, totals.isoCode)}
                </TableCell>
                <TableCell align="right">
                  {formatAgingAmount(totals.total, totals.isoCode)}
                </TableCell>
              </TableRow>
            ))}
          </TableFooter>
        )}
      </Table>
    </TableContainer>
  );
}

function AgingTableSkeletonRows() {
  return (
    <>
      {[0, 1, 2].map((i) => (
        <TableRow key={i}>
          <TableCell sx={{ width: 48 }} />
          {Array.from({ length: 7 }).map((_, idx) => (
            <TableCell key={idx}>
              <Skeleton variant="text" />
            </TableCell>
          ))}
        </TableRow>
      ))}
    </>
  );
}

// ---------------------------------------------------------------------------
// Expandable group row
// ---------------------------------------------------------------------------

function ExpandableGroupRow(props: {
  row: GroupedRow;
  isExpanded: boolean;
  onToggle: () => void;
}) {
  const { row, isExpanded, onToggle } = props;
  const rowTotal = sumAgingBuckets(row);

  // Defer building the detail sub-tree until the row has been opened at
  // least once. Without this gate, the .map() over row.invoices allocates
  // a full JSX tree (Link + 8 cells + Chip per invoice) on every parent
  // render — for a listing with hundreds of never-opened groups that
  // cost dominates bucket toggles, search keystrokes, and group-by flips
  // even though MUI's unmountOnExit keeps the detail rows off the DOM.
  // Once opened we keep rendering so the Collapse exit animation plays
  // and subsequent toggle cycles are instant.
  const [hasBeenExpanded, setHasBeenExpanded] = useState(isExpanded);
  if (isExpanded && !hasBeenExpanded) {
    setHasBeenExpanded(true);
  }

  return (
    <>
      {/* Summary row */}
      <TableRow hover onClick={onToggle} sx={{ cursor: 'pointer' }}>
        <TableCell sx={{ width: 48, paddingY: 0 }}>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onToggle();
            }}
            tabIndex={-1}
            aria-label={isExpanded ? 'Collapse invoices' : 'Expand invoices'}
            sx={{
              transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s',
            }}
          >
            <ExpandMoreRounded fontSize="small" />
          </IconButton>
        </TableCell>
        <TableCell>
          <Typography variant="body2" fontWeight="bold">
            {row.label}
          </Typography>
        </TableCell>
        <TableCell align="right">
          <BucketAmount
            value={row.current}
            currencyCode={row.isoCode}
            colorKey="current"
          />
        </TableCell>
        <TableCell align="right">
          <BucketAmount
            value={row.d1_30}
            currencyCode={row.isoCode}
            colorKey="d1_30"
          />
        </TableCell>
        <TableCell align="right">
          <BucketAmount
            value={row.d31_60}
            currencyCode={row.isoCode}
            colorKey="d31_60"
          />
        </TableCell>
        <TableCell align="right">
          <BucketAmount
            value={row.d61_90}
            currencyCode={row.isoCode}
            colorKey="d61_90"
          />
        </TableCell>
        <TableCell align="right">
          <BucketAmount
            value={row.d91}
            currencyCode={row.isoCode}
            colorKey="d91"
          />
        </TableCell>
        <TableCell align="right">
          <Typography variant="body2" fontWeight="bold">
            {formatAgingAmount(rowTotal, row.isoCode)}
          </Typography>
        </TableCell>
      </TableRow>

      {/* Expanded invoice detail */}
      <TableRow>
        <TableCell
          colSpan={8}
          sx={{ paddingY: 0, paddingBottom: isExpanded ? 2 : 0 }}
        >
          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
            {hasBeenExpanded ? (
              <InvoiceDetailSubtable
                invoices={row.invoices}
                detailDimensionLabel={row.detailDimensionLabel}
              />
            ) : null}
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

// Per-group invoice detail table. Memoized so collapsed groups fast-exit
// when the parent re-renders for unrelated reasons (bucket toggle, search
// keystroke, etc.) — the props are a reference to the same `invoices`
// array across renders (it comes straight out of the tier-3 memo) and the
// label is a stable string, so memo's default shallow-equal prop check
// lets the subtable skip reconciliation entirely.
const InvoiceDetailSubtable = memo(function InvoiceDetailSubtable(props: {
  invoices: InvoiceRow[];
  detailDimensionLabel: string;
}) {
  // The cross-dimension column shows whichever name is NOT the current
  // group label — reading it straight off the invoice row avoids any
  // row-level object mutation up in groupDataByDimension.
  const useCompanyColumn = props.detailDimensionLabel === 'Company';
  return (
    <Box sx={{ margin: 1 }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Invoice #</TableCell>
            <TableCell>{props.detailDimensionLabel}</TableCell>
            <TableCell>Invoice date</TableCell>
            <TableCell>Due date</TableCell>
            <TableCell align="right">Total</TableCell>
            <TableCell align="right">Balance</TableCell>
            <TableCell align="right">Days overdue</TableCell>
            <TableCell>Aging</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {props.invoices.map((inv) => {
            const chipProps = bucketChipProps(inv.bucket);
            return (
              <TableRow key={inv.key}>
                <TableCell>
                  <Link
                    to="/billing/invoices/$invoiceKey"
                    params={{ invoiceKey: inv.key }}
                    style={{ textDecoration: 'none' }}
                  >
                    <Typography
                      variant="body2"
                      fontWeight="bold"
                      color="primary.main"
                      sx={{ cursor: 'pointer' }}
                    >
                      {inv.invoiceNumber}
                    </Typography>
                  </Link>
                </TableCell>
                <TableCell>
                  {useCompanyColumn ? inv.company : inv.project}
                </TableCell>
                <TableCell>{inv.invoiceDate}</TableCell>
                <TableCell>{inv.dueDate}</TableCell>
                <TableCell align="right">
                  {formatAgingAmount(inv.total, inv.isoCode)}
                </TableCell>
                <TableCell align="right">
                  <Typography
                    variant="body2"
                    fontWeight="bold"
                    sx={{ color: invoiceBalanceColor(inv.bucket) }}
                  >
                    {formatAgingAmount(inv.balance, inv.isoCode)}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography
                    variant="body2"
                    sx={{
                      color:
                        inv.daysOverdue === 0
                          ? 'text.secondary'
                          : invoiceBalanceColor(inv.bucket),
                    }}
                  >
                    {inv.daysOverdue}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={inv.bucket}
                    size="small"
                    color={chipProps.color}
                    variant={chipProps.variant}
                  />
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Box>
  );
});
